import { z } from "zod";

// ============================================================================
// Transaction Hash Validation (Shared across EVM and Solana)
// ============================================================================

/** EVM transaction hash: 0x + 64 hex characters */
export const EVM_TX_HASH_REGEX = /^0x[a-fA-F0-9]{64}$/;

/** Solana transaction signature: 87-88 base58 characters (alphanumeric without 0, O, I, l) */
export const SOLANA_TX_SIGNATURE_REGEX = /^[1-9A-HJ-NP-Za-km-z]{87,88}$/;

/**
 * Validates a transaction hash for either EVM (0x...) or Solana (base58) format.
 * Use this function when you need to validate txHash outside of Zod schemas.
 */
export const isValidTxHash = (val: string): boolean =>
  EVM_TX_HASH_REGEX.test(val) || SOLANA_TX_SIGNATURE_REGEX.test(val);

/**
 * Zod schema for transaction hash validation.
 * Accepts both EVM (0x + 64 hex) and Solana (87-88 base58 chars) formats.
 *
 * @example
 * // EVM: 0x1234...abcd (66 chars total)
 * // Solana: 3B4Q8cDadSdXBEDN2HxUNM5rZK... (87-88 chars)
 */
export const txHashSchema = z
  .string()
  .refine(isValidTxHash, "Invalid transaction hash format (must be EVM 0x... or Solana base58)");

// ============================================================================
// Submit Transaction Schemas
// ============================================================================

/**
 * DTO for submitting an onchain transaction for indexing
 *
 * Note: For Solana chains, the backend automatically extracts the signer from the transaction
 * and verifies it against the user's Privy linked accounts (trustless verification).
 * No solanaWalletAddress field needed - we don't trust frontend input.
 */
export const submitOnchainTransactionSchema = z.object({
  txHash: txHashSchema,
  chainId: z.string(), // "8453" for Base, "42161" for Arbitrum, "solana" for Solana
  exchangeId: z.string(), // DEX exchange ID: "aerodrome-cl|base", "pancake-v3|arbitrum", "meteora-dyn|solana"
  campaignId: z.string().nullable(), // campaign slug - required for rewards tracking
});

export type SubmitOnchainTransaction = z.infer<typeof submitOnchainTransactionSchema>;

/**
 * Response for submitting an onchain transaction
 */
export const submitOnchainTransactionResponseSchema = z.object({
  success: z.boolean(),
  jobId: z.string(),
  txHash: z.string(),
  chainId: z.string(),
});

export type SubmitOnchainTransactionResponse = z.infer<typeof submitOnchainTransactionResponseSchema>;

// ============================================================================
// Wallet Address Validation (Shared across EVM and Solana)
// ============================================================================

/** EVM wallet address: 0x + 40 hex characters */
export const EVM_WALLET_ADDRESS_REGEX = /^0x[a-fA-F0-9]{40}$/;

/** Solana wallet address: 32-44 base58 characters (alphanumeric without 0, O, I, l) */
export const SOLANA_WALLET_ADDRESS_REGEX = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;

/**
 * Validates a wallet address for either EVM (0x...) or Solana (base58) format.
 * Use this function when you need to validate walletAddress outside of Zod schemas.
 */
export const isValidWalletAddress = (val: string): boolean =>
  EVM_WALLET_ADDRESS_REGEX.test(val) || SOLANA_WALLET_ADDRESS_REGEX.test(val);

/**
 * Zod schema for wallet address validation.
 * Accepts both EVM (0x + 40 hex) and Solana (32-44 base58 chars) formats.
 *
 * @example
 * // EVM: 0x1234...abcd (42 chars total)
 * // Solana: 7WDGm6dT... (32-44 chars)
 */
export const walletAddressSchema = z
  .string()
  .refine(isValidWalletAddress, "Invalid wallet address format (must be EVM 0x... or Solana base58)");

// ============================================================================
// Bot Submit Transaction Schema
// ============================================================================

/**
 * DTO for bot submission of onchain transactions
 * POST /v1/onchain-swap/bot/submit-transaction
 *
 * Used by trusted trading bots to submit transactions for indexing.
 * Requires private API key authentication.
 * Skips ownership verification since bots control their own wallets.
 */
export const botSubmitOnchainTransactionSchema = z.object({
  /** Transaction hash to index */
  txHash: txHashSchema,
  /** Chain ID (e.g., "8453" for Base) */
  chainId: z.string(),
  /** DEX exchange ID (e.g., "aerodrome-cl|base") */
  exchangeId: z.string(),
  /** Campaign ID for volume tracking */
  campaignId: z.string(),
  /** Wallet address that executed the transaction (EVM or Solana) */
  walletAddress: walletAddressSchema,
});

export type BotSubmitOnchainTransaction = z.infer<typeof botSubmitOnchainTransactionSchema>;
